import { useState, useEffect, useContext, useMemo } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import jwt_decode from 'jwt-decode';
import AppContext from '../../../context/HeaderContext';
import Loading from '../../../components/Loading';
import CenterCell from '../../../components/table/CenterCell';
import BasicPaginatedTable from '../../../components/table/BasicPaginatedTable';
import EditUser from './EditUser';
import { getAPI, putAPI, postAPI } from '../../../libs/fetchAPI';

function Users() {
	const {
		user,
		current_organization_id,
		updateSwitchUser
	} = useContext(AppContext);

	const [ loading, setLoading ]                     = useState(true);
	const [ loading_button, setLoadingButton ]        = useState({});
	const [ saving, setSaving ]                       = useState(false);
	const [ users, setUsers ]                         = useState([]);
	const [ project_options, setProjectOptions ]      = useState([]);
	const [ projects, setProjects ]                   = useState([]);
	const [ current_user, setCurrentUser ]            = useState({});
	const [ show_edit_modal, setShowEditModal ]       = useState(false);
	const [ edit_error_message, setEditErrorMessage ] = useState(false);

	useEffect(() => {
		setLoading(true);

		Promise.all([
			getAPI(`/users?organization_id=${current_organization_id}`),
			getAPI(`/projects?organization_id=${current_organization_id}`),
		])
		.then((response) => {
			const [ tmp_users, tmp_projects ] = response;

			const users = tmp_users.data.map(user => {
				user.projects    = user.project_users.map(project => project.title).join(`, `);
				user.project_ids = user.project_users.map(project => project.id);

				return user;
			});

			setProjects(tmp_projects.data);

			setProjectOptions(tmp_projects.data.map(project => {
				return {
					key   : project.id,
					text  : project.title,
					value : project.id,
				}
			}));

			setUsers(users);
			setLoading(false);
		});
	}, [current_organization_id]);

	function handleEditUser(user_id = null) {
		const user = !user_id ? {} : users.find(user => user.id === user_id);

		setShowEditModal(true);
		setCurrentUser(user);
	}

	function handleCloseEditModal() {
		setShowEditModal(false);
	}

	function updateCurrentUser(e, data = {}) {
		const update = {};

		// Checkbox
		if(data.type === `checkbox`) {
			update[data.name] = data.checked ? 1 : 0;
		}
		// Multi select
		else if(data.name === `projects`) {
			update.project_ids    = data.value;
			update.project_users  = [];
			const string_projects = [];

			for(const project_id of data.value) {
				for(const project of projects) {
					if(project.id !== project_id) {
						continue;
					}

					update.project_users.push({
						project_id,
						user_id : current_user.id,
						title   : project.title,
					});

					string_projects.push(project.title);
				}
			}

			update.projects = string_projects.join(`, `);
		}
		// All other inputs
		else {
			update[e.target.name] = e.target.value;
		}

		setCurrentUser({
			...current_user,
			...update,
		});
	}

	function handleSaveUser() {
		setSaving(true);
		setEditErrorMessage(false);

		let api_call = null;

		if(current_user.id) {
			api_call = putAPI(`/users/${current_user.id}`, current_user);
		}
		else {
			api_call = postAPI(`/users?organization_id=${current_organization_id}`, current_user);
		}

		api_call.then((response) => {
			if(response.error) {
				setEditErrorMessage(response.error);
				setSaving(false);

				return;
			}

			if(current_user.id) {
				setUsers(users.map(user => user.id === current_user.id ? current_user : user));
			}
			else {
				setUsers([...users, response.data[0]])
			}

			setShowEditModal(false);
			setCurrentUser({});
			setSaving(false);
		});
	}

	function handleLoginAs(user_id) {
		setLoadingButton({ [user_id] : true });

		postAPI(`/login_as`, { user_id })
			.then(response => {
				const decoded = jwt_decode(response.jwt);

				updateSwitchUser(decoded.sub.user, response.jwt, true);
				setLoadingButton({});

				window.location = '/';
			});
	}

	/* eslint-disable */
	const columns = useMemo(() => [
		{
			Header   : `Name`,
			accessor : `first_name`,
			Cell : ({ data, cell }) => {
				const id = cell.row.values.id;
				const user = data.find(user => user.id === id);

				return <span>{user.first_name} {user.last_name}</span>
			}
		},
		{
			Header   : `Email`,
			accessor : `email`,
		},
		{
			Header   : `Projects`,
			accessor : `projects`,
		},
		{
			Header         : `Active`,
			accessor       : `active`,
			disableFilters : true,
			Cell           : ({ cell }) => <CenterCell>{cell.value ? <Icon color="green" name="check" /> : <Icon color="red" name="close" />}</CenterCell>
		},
		{
			Header         : `Admin`,
			accessor       : `admin`,
			disableFilters : true,
			Cell           : ({ cell }) => <CenterCell>{cell.value ? <Icon color="green" name="check" /> : <Icon color="red" name="close" />}</CenterCell>
		},
		{
			Header         : ``,
			accessor       : `id`,
			disableFilters : true,
			Cell           : ({ cell }) => (
				<CenterCell>
					<Button primary onClick={() => handleEditUser(cell.value)}>Edit</Button>

					{user.super_user &&
						<Button loading={loading_button[cell.value]} color="teal" onClick={() => handleLoginAs(cell.value)}>Login As</Button>
					}
				</CenterCell>
			)
		}
	], [loading_button, users]);
	/* eslint-enable */

	if(loading) {
		return <Loading />
	}

	return (
		<>
			<EditUser
				current_user={current_user}
				project_options={project_options}
				open={show_edit_modal}
				saving={saving}
				handleClose={handleCloseEditModal}
				handleSaveUser={handleSaveUser}
				updateCurrentUser={updateCurrentUser}
				error_message={edit_error_message}
			/>

			<Button
				positive
				onClick={() => handleEditUser()}
			>
				Add New User
			</Button>

			<BasicPaginatedTable
				columns={columns}
				data={users}
			/>
		</>
	);
}

export default Users;
